//@ts-nocheck
import React, { useState } from 'react';
import {
	EditBase,
	TopToolbar,
	useRecordContext,
	useNotify,
	SimpleForm,
	TextInput,
	SelectInput,
	Button as RaButton,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import {
	Card,
	CardContent,
	Grid,
	Typography,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	FormControlLabel,
	Checkbox,
	TextField as MuiTextField,
	Stack,
	CircularProgress,
	Box,
} from '@mui/material';
import { Route, Routes, useNavigate, useParams } from 'react-router';
import HomeIcon from '@mui/icons-material/Home';
import LinkIcon from '@mui/icons-material/Link';
import CodeIcon from '@mui/icons-material/Code';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HistoryIcon from '@mui/icons-material/History';
import SyncIcon from '@mui/icons-material/Sync';
import { ConnectorAccountYml } from './ConnectorAccountYml';
import { ConnectorAccountLogs } from './ConnectorAccountLogs';
import { ConnectorRequestList } from './ConnectorRequestList';
import { ActivityCard } from '../Activities/ActivityCard';
import { forceBatch, historyBatch } from '../../Services/connectorAccountProcess';
import { syncConnectorSchema } from '../../Services/quantiAI';

// #region InfoCard
const InfoCard = () => {
	const record = useRecordContext();

	if (!record) return null;

	const infoItems = [
		{ label: 'Account Name', value: record.accountName },
		{ label: 'Account ID', value: record.accountId },
		{ label: 'Project Folder', value: record.projectFolderId },
		...(record.project?.name ? [{ label: 'Project Name', value: record.project.name }] : []),
		{ label: 'SKU Parent', value: record.skuParent },
		{ label: 'SKU', value: record.sku },
	];

	return (
		<Card>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Info
				</Typography>
				<Stack spacing={0.5}>
					{infoItems.map((item) => (
						<Box key={item.label}>
							<Typography variant="caption" color="text.secondary">
								{item.label}
							</Typography>
							<Typography variant="body2">{item.value || '—'}</Typography>
						</Box>
					))}
				</Stack>
			</CardContent>
		</Card>
	);
};
// #endregion

// #region VersionFields
const VersionFields = () => {
	const processVersion = useWatch({ name: 'processVersion' });

	if (processVersion === '2.0') {
		return (
			<>
				<TextInput source="gitProjectId" label="Git Project ID" fullWidth size="small" />
				<TextInput source="version" label="Version" fullWidth size="small" />
			</>
		);
	}

	return null;
};
// #endregion

// #region EditFormCard
const EditFormCard = () => {
	return (
		<Card>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Configuration
				</Typography>
				<SimpleForm redirect={false}>
					<TextInput source="databaseName" fullWidth size="small" />
					<SelectInput
						source="processVersion"
						label="Process Version"
						choices={[
							{ id: '1.0', name: '1.0' },
							{ id: '2.0', name: '2.0' },
						]}
						fullWidth
						size="small"
					/>
					<VersionFields />
				</SimpleForm>
			</CardContent>
		</Card>
	);
};
// #endregion

const DEFAULT_PROCESSOR_IMAGE = 'rg.fr-par.scw.cloud/core-services/processor-v2:latest-dev';

// #region ForceModal
interface ForceModalProps {
	open: boolean;
	onClose: () => void;
	onConfirm: (cleanupOldProcessData: boolean, testMode: boolean, connectorVersion?: string, processorVersion?: string) => void;
	loading: boolean;
}

const ForceModal = ({ open, onClose, onConfirm, loading }: ForceModalProps) => {
	const [cleanupOldProcessData, setCleanupOldProcessData] = useState(false);
	const [testMode, setTestMode] = useState(false);
	const [connectorVersion, setConnectorVersion] = useState('');
	const [processorVersion, setProcessorVersion] = useState('');

	const handleConfirm = () => {
		const effectiveProcessorVersion = testMode && !processorVersion ? DEFAULT_PROCESSOR_IMAGE : processorVersion || undefined;
		onConfirm(cleanupOldProcessData, testMode, connectorVersion || undefined, effectiveProcessorVersion);
	};

	const handleClose = () => {
		setCleanupOldProcessData(false);
		setTestMode(false);
		setConnectorVersion('');
		setProcessorVersion('');
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Lancer Force</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					<FormControlLabel
						control={
							<Checkbox
								checked={cleanupOldProcessData}
								onChange={(e) => setCleanupOldProcessData(e.target.checked)}
							/>
						}
						label="Nettoyer les anciennes données (cleanup)"
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={testMode}
								onChange={(e) => setTestMode(e.target.checked)}
								color="warning"
							/>
						}
						label="Mode Test E2E (écriture dans BQ de test)"
					/>
					{testMode && (
						<>
							<MuiTextField
								label="Version du connecteur (optionnel)"
								value={connectorVersion}
								onChange={(e) => setConnectorVersion(e.target.value)}
								placeholder="ex: v1.2.3 ou latest"
								fullWidth
								size="small"
							/>
							<MuiTextField
								label="Version du processor (optionnel)"
								value={processorVersion}
								onChange={(e) => setProcessorVersion(e.target.value)}
								placeholder="ex: latest-dev"
								fullWidth
								size="small"
							/>
						</>
					)}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Annuler
				</Button>
				<Button
					onClick={handleConfirm}
					variant="contained"
					color={testMode ? 'warning' : 'primary'}
					disabled={loading}
					startIcon={loading ? <CircularProgress size={16} /> : <PlayArrowIcon />}
				>
					{testMode ? 'Lancer (Test E2E)' : 'Lancer'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
// #endregion

// #region HistoryModal
interface HistoryModalProps {
	open: boolean;
	onClose: () => void;
	onConfirm: (startDate: string, endDate: string, cleanupOldProcessData: boolean, testMode: boolean, connectorVersion?: string, processorVersion?: string) => void;
	loading: boolean;
}

const HistoryModal = ({ open, onClose, onConfirm, loading }: HistoryModalProps) => {
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [cleanupOldProcessData, setCleanupOldProcessData] = useState(false);
	const [testMode, setTestMode] = useState(false);
	const [connectorVersion, setConnectorVersion] = useState('');
	const [processorVersion, setProcessorVersion] = useState('');

	const handleConfirm = () => {
		if (!startDate || !endDate) return;
		const start = new Date(startDate).toISOString();
		const end = new Date(endDate).toISOString();
		const effectiveProcessorVersion = testMode && !processorVersion ? DEFAULT_PROCESSOR_IMAGE : processorVersion || undefined;
		onConfirm(start, end, cleanupOldProcessData, testMode, connectorVersion || undefined, effectiveProcessorVersion);
	};

	const handleClose = () => {
		setStartDate('');
		setEndDate('');
		setCleanupOldProcessData(false);
		setTestMode(false);
		setConnectorVersion('');
		setProcessorVersion('');
		onClose();
	};

	const isValid = startDate && endDate && new Date(startDate) <= new Date(endDate);

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Reprise d'historique</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					<MuiTextField
						label="Date de début"
						type="date"
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
						InputLabelProps={{ shrink: true }}
						fullWidth
					/>
					<MuiTextField
						label="Date de fin"
						type="date"
						value={endDate}
						onChange={(e) => setEndDate(e.target.value)}
						InputLabelProps={{ shrink: true }}
						fullWidth
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={cleanupOldProcessData}
								onChange={(e) => setCleanupOldProcessData(e.target.checked)}
							/>
						}
						label="Nettoyer les anciennes données (cleanup)"
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={testMode}
								onChange={(e) => setTestMode(e.target.checked)}
								color="warning"
							/>
						}
						label="Mode Test E2E (écriture dans BQ de test)"
					/>
					{testMode && (
						<>
							<MuiTextField
								label="Version du connecteur (optionnel)"
								value={connectorVersion}
								onChange={(e) => setConnectorVersion(e.target.value)}
								placeholder="ex: v1.2.3 ou latest"
								fullWidth
								size="small"
							/>
							<MuiTextField
								label="Version du processor (optionnel)"
								value={processorVersion}
								onChange={(e) => setProcessorVersion(e.target.value)}
								placeholder="ex: latest-dev"
								fullWidth
								size="small"
							/>
						</>
					)}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Annuler
				</Button>
				<Button
					onClick={handleConfirm}
					variant="contained"
					color={testMode ? 'warning' : 'primary'}
					disabled={loading || !isValid}
					startIcon={loading ? <CircularProgress size={16} /> : <HistoryIcon />}
				>
					{testMode ? 'Lancer (Test E2E)' : 'Lancer'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
// #endregion

// #region ConnectorAccountActions
const ConnectorAccountActions = () => {
	const record = useRecordContext();
	const navigate = useNavigate();
	const notify = useNotify();

	const [forceModalOpen, setForceModalOpen] = useState(false);
	const [historyModalOpen, setHistoryModalOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [syncLoading, setSyncLoading] = useState(false);

	if (!record) return null;

	const handleOpenProject = () => {
		const w: any = window;
		const skuParent = record.skuParent || 'advertiser';
		const url = `${w.FRONT_URL}/${record.projectFolderId}/${skuParent}/${record.sku}/details/${record.accountId}`;
		window.open(url, '_blank');
	};

	const handleOpenYml = () => {
		navigate('yml');
	};

	const handleGoHome = () => {
		navigate(`/connector-accounts/${record.projectFolderId}/${record.sku}/${record.accountId}`);
	};

	const handleForceConfirm = async (cleanupOldProcessData: boolean, testMode: boolean, connectorVersion?: string, processorVersion?: string) => {
		setLoading(true);
		try {
			const results = await forceBatch({
				connectorAccounts: [
					{
						projectFolderId: record.projectFolderId,
						sku: record.sku,
						accountId: record.accountId,
					},
				],
				cleanupOldProcessData,
				testMode,
				connectorVersion,
				processorVersion,
			});

			if (results[0]?.success) {
				notify(testMode ? 'Force E2E lancé avec succès' : 'Force lancé avec succès', { type: 'success' });
			} else {
				notify(`Erreur: ${results[0]?.error || 'Unknown'}`, { type: 'error' });
			}
			setForceModalOpen(false);
		} catch (error: any) {
			notify(error.message || 'Erreur lors du force', { type: 'error' });
		} finally {
			setLoading(false);
		}
	};

	const handleHistoryConfirm = async (
		startDate: string,
		endDate: string,
		cleanupOldProcessData: boolean,
		testMode: boolean,
		connectorVersion?: string,
		processorVersion?: string
	) => {
		setLoading(true);
		try {
			const results = await historyBatch({
				connectorAccounts: [
					{
						projectFolderId: record.projectFolderId,
						sku: record.sku,
						accountId: record.accountId,
					},
				],
				startDate,
				endDate,
				cleanupOldProcessData,
				testMode,
				connectorVersion,
				processorVersion,
			});

			if (results[0]?.success) {
				notify(testMode ? 'Historique E2E lancé avec succès' : 'Historique lancé avec succès', { type: 'success' });
			} else {
				notify(`Erreur: ${results[0]?.error || 'Unknown'}`, { type: 'error' });
			}
			setHistoryModalOpen(false);
		} catch (error: any) {
			notify(error.message || "Erreur lors de la reprise d'historique", { type: 'error' });
		} finally {
			setLoading(false);
		}
	};

	const handleSyncToQdrant = async () => {
		setSyncLoading(true);
		try {
			const result = await syncConnectorSchema(
				record.projectFolderId,
				record.sku,
				record.accountId
			);
			if (result.success) {
				notify(`Sync Qdrant OK: ${result.tables_indexed} tables indexées`, { type: 'success' });
			} else {
				notify(`Erreur: ${result.error || 'Unknown'}`, { type: 'error' });
			}
		} catch (error: any) {
			notify(error.message || 'Erreur lors du sync Qdrant', { type: 'error' });
		} finally {
			setSyncLoading(false);
		}
	};

	return (
		<>
			<TopToolbar>
				<RaButton label="Home" onClick={handleGoHome}>
					<HomeIcon />
				</RaButton>
				<RaButton label="Go to connector" onClick={handleOpenProject}>
					<LinkIcon />
				</RaButton>
				<RaButton label="Yml" onClick={handleOpenYml}>
					<CodeIcon />
				</RaButton>
				<RaButton label="Force" onClick={() => setForceModalOpen(true)}>
					<PlayArrowIcon />
				</RaButton>
				<RaButton label="Historique" onClick={() => setHistoryModalOpen(true)}>
					<HistoryIcon />
				</RaButton>
				<RaButton label="Sync Qdrant" onClick={handleSyncToQdrant} disabled={syncLoading}>
					{syncLoading ? <CircularProgress size={18} /> : <SyncIcon />}
				</RaButton>
			</TopToolbar>
			<ForceModal
				open={forceModalOpen}
				onClose={() => setForceModalOpen(false)}
				onConfirm={handleForceConfirm}
				loading={loading}
			/>
			<HistoryModal
				open={historyModalOpen}
				onClose={() => setHistoryModalOpen(false)}
				onConfirm={handleHistoryConfirm}
				loading={loading}
			/>
		</>
	);
};
// #endregion

// #region SidebarContent
const SidebarContent = () => {
	return (
		<>
			<InfoCard />
			<Box sx={{ mt: 2 }}>
				<EditFormCard />
			</Box>
		</>
	);
};
// #endregion

// #region ConnectorAccountHome
export const ConnectorAccountHome = (props: any) => {
	const { projectFolderId, sku, accountId } = useParams();

	// id "virtuel" pour RA (unique, stable) — l'API n'en a pas besoin
	const raId = `${projectFolderId}-${sku}-${accountId}`;

	const meta = { projectFolderId, sku, accountId };

	return (
		<EditBase
			{...props}
			id={raId}
			resource="connector-accounts"
			queryOptions={{ meta }}
			mutationOptions={{ meta }}
			mutationMode="pessimistic"
			redirect={false}
		>
			<>
				<ConnectorAccountActions />
				<Grid container spacing={2}>
					<Grid item xs={8}>
						<Routes>
							<Route
								index
								element={
									<Stack spacing={2}>
										<ConnectorRequestList />
										<ConnectorAccountLogs />
										{/* Activité HUMAINE sur ce compte (configuration, activation,
										    credentials) — à ne pas confondre avec ConnectorAccountLogs
										    juste au-dessus, qui montre les exécutions de synchro. */}
										<ActivityCard
											filters={{
												projectFolderId,
												objectType: 'connector',
												objectId: sku,
												// Indispensable : un même SKU peut avoir plusieurs
												// comptes dans un projet, sans lui on afficherait
												// l'activité des autres.
												accountId,
											}}
											title="Activité du compte"
											showProject={false}
										/>
									</Stack>
								}
							/>
							<Route path="yml" element={<ConnectorAccountYml />} />
						</Routes>
					</Grid>
					<Grid item xs={4}>
						<SidebarContent />
					</Grid>
				</Grid>
			</>
		</EditBase>
	);
};
// #endregion
